import type {ReactElement} from 'react';
import React from 'react';
import {DEFAULT_CURVED_ROUNDESS, EdgeType} from '@PosterWhiteboard/classes/item-effects.class';
import {BaseImageRoundnessOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/base-image-roundness-option/base-image-roundness-option';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';
import {useGetKeyFromEffectsActiveItem} from '@Hooks/poster-editor/useGetKeyFromEffectsOfActiveItem';

interface ImageRoundnessOptionProps {
  isInPanel?: boolean;
  panelId?: string;
}

export function ImageRoundnessOption({isInPanel = false, panelId = ''}: ImageRoundnessOptionProps): ReactElement | null {
  const activeItemId = useSingleActivePosterItemId() ?? '';
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;
  const edgeType = useGetKeyFromEffectsActiveItem('edgeType');
  const activeItemEdgeThickness = useGetKeyFromEffectsActiveItem('edgeThickness');

  if (activeItem?.isImage() && activeItem.isDrawing()) {
    return null;
  }

  if (!activeItem || (activeItem && !('effects' in activeItem)) || activeItemEdgeThickness === undefined || edgeType === undefined) {
    throw new Error(`Item doesn't have effects property`);
  }

  const isCurvedApplied = (): boolean => {
    return Number(edgeType) === Number(EdgeType.CURVED);
  };

  const onToggleCurved = async (): Promise<void> => {
    const isApplied = isCurvedApplied();
    const edgeTypeToApply = isApplied ? EdgeType.NONE : EdgeType.CURVED;

    await activeItem.updateFromObject({
      effects: {
        edgeType: edgeTypeToApply,
      },
      border: {
        solidBorderType: activeItem.effects.getBorderForEdgeEffect(edgeTypeToApply),
      },
    });

    if (!isApplied) {
      await activeItem.updateFromObject(
        {
          effects: {
            edgeThickness: DEFAULT_CURVED_ROUNDESS,
          },
        },
        {undoable: false}
      );
    }
  };

  const onUpdateThickness = async (value: number, undoable = false): Promise<void> => {
    await activeItem.updateFromObject(
      {
        effects: {
          edgeThickness: value,
        },
      },
      {undoable}
    );
  };

  return (
    <BaseImageRoundnessOption
      panelId={panelId}
      isChecked={isCurvedApplied()}
      onToggleCurved={(): void => {
        void onToggleCurved();
      }}
      edgeThickness={activeItemEdgeThickness}
      onUpdateThickness={(value, undoable): void => {
        void onUpdateThickness(value, undoable);
      }}
      isOpenedInPanel={isInPanel}
    />
  );
}
